/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { cn } from '@/lib/utils'

export type NavBadgeSize = 'sm' | 'md'

export interface NavBadgeProps {
  badge?: number | boolean
  /** @default 'sm' */
  size?: NavBadgeSize
  className?: string
}

const BADGE_SIZES: Record<NavBadgeSize, { count: string; dot: string; right: string; top: string }> = {
  sm: {
    count: 'h-[16px] min-w-[16px] px-1',
    dot: 'size-[7px]',
    right: '-right-[5px]',
    top: '-top-[3px]',
  },
  md: {
    count: 'h-5 min-w-5 px-1.5',
    dot: 'size-2',
    right: '-right-1.5',
    top: '-top-0.5',
  },
}

export function NavBadge({ badge, size = 'sm', className }: NavBadgeProps) {
  const hasBadge = badge === true || (typeof badge === 'number' && badge > 0)
  if (!hasBadge) {
    return null
  }

  const badgeCount = typeof badge === 'number' ? badge : undefined
  const s = BADGE_SIZES[size]
  const isDot = badgeCount == null

  return (
    <span
      className={cn(
        'pointer-events-none absolute flex items-center justify-center rounded-full bg-primary font-semibold text-primary-foreground text-xs leading-none',
        isDot ? s.dot : s.count,
        s.right,
        s.top,
        className
      )}
    >
      {badgeCount != null && badgeCount <= 99 ? badgeCount : badgeCount != null ? '99+' : null}
    </span>
  )
}
